import i18n from '@/locale'
import { activityListRepeat, registerActivity, headquartersCouponBlacklist } from '@/api/hqEvent'

/** 到期检查间隔：10 分钟 */
const EXPIRE_CHECK_INTERVAL = 10 * 60 * 1000

/** 模块内定时器，不入 Vuex state */
let expireTimer = null
/** 服务端时间相对本地的偏移（systemTime - Date.now()），无 systemTime 时为 0 */
let serverTimeOffset = 0

const ACTIVITY_STATUS = {
  IN_PROGRESS: 'IN_PROGRESS',
  ENDED: 'ENDED',
}

const SHOP_PARTICIPATE_REGISTERED = 'REGISTERED_SHOPS'

/**
 * 枚举字段兼容字符串和 { code, label }
 * @param {string|{code?: string}|number} value
 * @returns {string}
 */
function enumCode(value) {
  if (value == null || value === '') return ''
  if (typeof value === 'string' || typeof value === 'number') return String(value)
  if (typeof value === 'object') return value.code || ''
  return ''
}

/**
 * 解析当前时间：优先服务端 systemTime，否则 Date.now()
 * @param {number|string} [systemTime]
 * @returns {number}
 */
function resolveNow(systemTime) {
  const ts = Number(systemTime)
  if (ts > 0) return ts
  return Date.now() + serverTimeOffset
}

/**
 * 用接口 systemTime 校准本地偏移，供后续到期检查使用
 * @param {number|string} [systemTime]
 */
function syncServerTimeOffset(systemTime) {
  const ts = Number(systemTime)
  serverTimeOffset = ts > 0 ? ts - Date.now() : 0
}

/**
 * 格式化为 M月D日
 * @param {number|string} ts
 * @returns {string}
 */
function formatMd(ts) {
  if (ts == null || ts === '') return ''
  return dayjs(ts).format('M月D日')
}

/**
 * 将活动项组装为 banner / dialog 展示数据
 * @param {Object} activity
 * @returns {Object}
 */
function buildBannerPayload(activity) {
  const beginText = formatMd(activity.activityBegin)
  const endText = formatMd(activity.activityEnd)
  const dateRange = beginText && endText ? `${beginText}-${endText}` : beginText || endText
  const dateText = dateRange
  const activityName = activity.activityName || activity.displayName || ''
  const thresholdAmt = activity.thresholdAmt
  const rebateAmt = activity.rebateAmt
  const ruleText = activity.ruleDesc || ''
  const image = activity.activityImageUrl || ''

  const tag =
    thresholdAmt != null && rebateAmt != null
      ? `${i18n.t('满')}${thresholdAmt}${i18n.t('减')}${rebateAmt}`
      : ''
  const content = ruleText
  const dialogTitle = activityName
  const dialogDateText = dateRange ? `${i18n.t('活动时间')}：${beginText} - ${endText}` : ''
  const dialogDescription = ruleText
  const dialogImage = image

  return {
    dateText,
    activityName,
    tag,
    content,
    gradient: '',
    activityEnd: Number(activity.activityEnd) || 0,
    dialogTitle,
    dialogDateText,
    dialogDescription,
    dialogImage,
  }
}

function stopExpireCheck() {
  if (expireTimer) {
    clearInterval(expireTimer)
    expireTimer = null
  }
}

/**
 * 仅有进行中活动时启动每 10 分钟到期检查
 * @param {Function} dispatch
 */
function startExpireCheck(dispatch) {
  stopExpireCheck()
  expireTimer = setInterval(() => {
    dispatch('CHECK_ACTIVITY_EXPIRE')
  }, EXPIRE_CHECK_INTERVAL)
}

const state = () => ({
  list: [],
  visible: false,
  dateText: '',
  activityName: '',
  tag: '',
  content: '',
  gradient: '',
  /** 当前展示活动的结束时间戳，用于到期检查 */
  activityEnd: 0,
  /** 当前展示活动编码，报名入参 */
  activityCode: '',
  /** 参加活动门店类型 code */
  shopParticipateType: '',
  /** 当前门店是否已报名 */
  registered: false,
  /** 当前展示活动状态 code */
  activityStatus: '',
  /** 当前门店/用户是否在总部活动黑名单；接口失败也视为 true */
  blacklisted: false,
  // 活动提示弹窗
  dialogVisible: false,
  dialogTitle: '',
  dialogDateText: '',
  dialogDescription: '',
  dialogImage: '',
})

const mutations = {
  SET_LIST(state, list = []) {
    state.list = Array.isArray(list) ? list : []
  },
  SET_EVENT_BANNER(
    state,
    {
      dateText = '',
      activityName = '',
      tag = '',
      content = '',
      gradient = '',
      activityEnd = 0,
      activityCode = '',
      shopParticipateType = '',
      registered = false,
      activityStatus = '',
    } = {}
  ) {
    state.dateText = dateText || ''
    state.activityName = activityName || ''
    state.tag = tag || ''
    state.content = content || ''
    state.gradient = gradient || ''
    state.activityEnd = Number(activityEnd) || 0
    state.activityCode = activityCode || ''
    state.shopParticipateType = shopParticipateType || ''
    state.registered = registered === true
    state.activityStatus = activityStatus || ''
    state.visible = Boolean(
      (dateText && String(dateText).trim()) ||
        (activityName && String(activityName).trim()) ||
        (content && String(content).trim())
    )
  },
  SET_EVENT_DIALOG(
    state,
    { visible = false, title = '', dateText = '', description = '', image = '' } = {}
  ) {
    state.dialogTitle = title || ''
    state.dialogDateText = dateText || ''
    state.dialogDescription = description || ''
    state.dialogImage = image || ''
    state.dialogVisible = Boolean(visible)
  },
  SET_EVENT_DIALOG_VISIBLE(state, visible) {
    state.dialogVisible = Boolean(visible)
  },
  SET_REGISTERED(state, registered) {
    state.registered = registered === true
    const code = state.activityCode
    if (!code) return
    const item = state.list.find((row) => (row.activityCode || row.couponCode) === code)
    if (item) item.registered = state.registered
  },
  SET_BLACKLISTED(state, blacklisted) {
    state.blacklisted = blacklisted === true
  },
  CLEAR_EVENT_BANNER(state) {
    state.visible = false
    state.dateText = ''
    state.activityName = ''
    state.tag = ''
    state.content = ''
    state.gradient = ''
    state.activityEnd = 0
    state.activityCode = ''
    state.shopParticipateType = ''
    state.registered = false
    state.activityStatus = ''
    state.dialogVisible = false
    state.dialogTitle = ''
    state.dialogDateText = ''
    state.dialogDescription = ''
    state.dialogImage = ''
  },
}

const actions = {
  /**
   * 查询总部活动黑名单；true 或接口失败均视为黑名单并清空活动
   * @returns {Promise<boolean>} 是否黑名单
   */
  async CHECK_HQ_BLACKLIST({ commit, dispatch }) {
    try {
      const { data } = await headquartersCouponBlacklist()
      const isBlacklisted = data === true
      commit('SET_BLACKLISTED', isBlacklisted)
      if (isBlacklisted) {
        // CLEAR 不清 blacklisted，保持 true 供 hit 门禁使用
        await dispatch('CLEAR_EVENT_BANNER')
      }
      return isBlacklisted
    } catch (error) {
      // 失败当黑名单：不调 options/hit，清空已有活动
      console.error('TIPS CHECK_HQ_BLACKLIST error:', error)
      commit('SET_BLACKLISTED', true)
      await dispatch('CLEAR_EVENT_BANNER')
      return true
    }
  },

  /**
   * 拉取活动列表，仅展示进行中活动；失败不更新中心
   * 黑名单或黑名单接口失败时不请求 options
   */
  async FETCH_EVENT_BANNER({ commit, dispatch }) {
    const isBlacklisted = await dispatch('CHECK_HQ_BLACKLIST')
    if (isBlacklisted) {
      return { data: null }
    }

    try {
      const res = await activityListRepeat()
      const list = Array.isArray(res?.data) ? res.data : []
      // systemTime 在每条活动上；取列表中有值的第一条即可
      const systemTime = list.find((item) => item?.systemTime != null)?.systemTime
      syncServerTimeOffset(systemTime)
      commit('SET_LIST', list)

      const inProgress = list.find((item) => item && enumCode(item.status) === ACTIVITY_STATUS.IN_PROGRESS)
      if (!inProgress) {
        stopExpireCheck()
        commit('CLEAR_EVENT_BANNER')
        return { data: null }
      }

      // 再次校验结束时间，避免状态滞后（有 systemTime 用服务端时间）
      const activityEnd = Number(inProgress.activityEnd) || 0
      const now = resolveNow(systemTime ?? inProgress.systemTime)
      if (activityEnd > 0 && now >= activityEnd) {
        stopExpireCheck()
        commit('CLEAR_EVENT_BANNER')
        return { data: null }
      }

      const payload = buildBannerPayload(inProgress)
      commit('SET_EVENT_BANNER', {
        dateText: payload.dateText,
        activityName: payload.activityName,
        tag: payload.tag,
        content: payload.content,
        gradient: payload.gradient,
        activityEnd: payload.activityEnd,
        activityCode: inProgress.activityCode || inProgress.couponCode || '',
        shopParticipateType: enumCode(inProgress.shopParticipateType),
        registered: inProgress.registered === true,
        activityStatus: enumCode(inProgress.status),
      })
      commit('SET_EVENT_DIALOG', {
        visible: false,
        title: payload.dialogTitle,
        dateText: payload.dialogDateText,
        description: payload.dialogDescription,
        image: payload.dialogImage,
      })

      startExpireCheck(dispatch)
      return { data: inProgress }
    } catch (error) {
      // 失败不更新中心，保留上次成功数据与定时器
      console.error('TIPS FETCH_EVENT_BANNER error:', error)
      throw error
    }
  },

  /**
   * 每 10 分钟检查活动是否已结束（沿用最近一次 systemTime 校准后的时间）
   */
  CHECK_ACTIVITY_EXPIRE({ state, commit }) {
    const activityEnd = Number(state.activityEnd) || 0
    if (!state.visible || !activityEnd) {
      stopExpireCheck()
      return
    }
    if (resolveNow() >= activityEnd) {
      stopExpireCheck()
      commit('CLEAR_EVENT_BANNER')
    }
  },

  CLEAR_EVENT_BANNER({ commit }) {
    stopExpireCheck()
    serverTimeOffset = 0
    commit('CLEAR_EVENT_BANNER')
    commit('SET_LIST', [])
    // 不重置 blacklisted：黑名单拦截清空活动后需保持 true；登出后由下次 CHECK 覆盖
  },

  OPEN_EVENT_DIALOG({ commit, state }) {
    const hasDialog =
      Boolean(state.dialogTitle && String(state.dialogTitle).trim()) ||
      Boolean(state.dialogImage && String(state.dialogImage).trim()) ||
      Boolean(
        state.dialogDescription &&
          (Array.isArray(state.dialogDescription)
            ? state.dialogDescription.length
            : String(state.dialogDescription).trim())
      )
    if (hasDialog) {
      commit('SET_EVENT_DIALOG_VISIBLE', true)
    }
  },

  CLOSE_EVENT_DIALOG({ commit }) {
    commit('SET_EVENT_DIALOG_VISIBLE', false)
  },

  /**
   * 当前横幅活动报名。失败不改本地状态，成功后标记已报名并隐藏按钮。
   */
  async REGISTER_ACTIVITY({ state, commit }) {
    const activityCode = state.activityCode
    if (!activityCode || state.registered) return false
    const res = await registerActivity({ activityCode })
    if (res?.data !== true) return false
    commit('SET_REGISTERED', true)
    return true
  },
}

const getters = {
  visible: (state) => state.visible,
  dateText: (state) => state.dateText,
  activityName: (state) => state.activityName,
  tag: (state) => state.tag,
  content: (state) => state.content,
  gradient: (state) => state.gradient,
  dialogVisible: (state) => state.dialogVisible,
  dialogTitle: (state) => state.dialogTitle,
  dialogDateText: (state) => state.dialogDateText,
  dialogDescription: (state) => state.dialogDescription,
  dialogImage: (state) => state.dialogImage,
  /** 当前门店是否已报名当前活动 */
  registered: (state) => state.registered,
  /** 是否在总部活动黑名单（含接口失败视为黑名单） */
  blacklisted: (state) => state.blacklisted === true,
  /**
   * 报名门店、当前门店未报名、活动未结束时展示报名
   */
  canRegister: (state) =>
    state.visible &&
    state.shopParticipateType === SHOP_PARTICIPATE_REGISTERED &&
    state.registered === false &&
    state.activityStatus !== ACTIVITY_STATUS.ENDED &&
    !!state.activityCode &&
    state.blacklisted !== true,
}

export default { state, mutations, actions, getters }
